import { FC, useEffect, useMemo, useRef, useState } from "react";
import { Box, Divider, Modal, Stack, styled, Typography } from "@mui/material";
import { FormProvider, useForm } from "react-hook-form";
import { Button } from "@/components/elements/Button";
import { Select } from "@/components/elements/Select";
import { InputDatePicker } from "@/components/elements/InputDatePicker";
import { IconBox } from "@/components/elements/IconBox";
import { IconCheck, IconClose } from "@/assets/icons";
import { useGetIndustryCurriculumList } from "@/api/services/main/industry";
import { useLearningProgressExamList } from "@/api/services/main/learning-progress";
import { calculateAvailableLearningWeeks, getNextOrCurrentMonday } from "@/features/learner/utils/calculates";
import { MOCK_EXAM_WEEKS } from "@/constants/common";
import { EXAM_METHOD, EXAM_STATUS } from "@/types/enum";
import { TBulkImportRow } from "@/features/learner/components/learner-bulk-import/bulk-import-mock";
import { background, black, button, divider, primary, text } from "@/theme/colors";
import { rounded } from "@/theme/variables";
import { FCC, TOption } from "@/types/common";
import { dayjs } from "@/utils/dayjs";
import { isDayjs } from "dayjs";

// 一括変更の「変更モード」。既定は keep(変更しない) で、選んだ項目だけ反映する (spec No.2)。
type TChangeMode = "keep" | "on" | "off";

// 受験予定の「変更しない」を表すセンチネル。実 examId(数値) と区別する。
const EXAM_KEEP = "keep";

type Props = {
  open: boolean;
  // 対象件数 (チェック済みの行数)。
  targetCount: number;
  // 選択行は同一受験分野に限定される (table の No.34 制限) ため、共通の industryId を受け取る。
  industryId: number | null;
  onClose: () => void;
  // 確定した差分パッチを親へ渡す (選択行すべてに適用する)。
  onApply: (patch: Partial<TBulkImportRow>) => void;
};

// 手続き管理 / 学習アプリ で使う 3 値セグメント (変更しない / すべて有効 / すべて無効)。
const SEGMENTS: { value: TChangeMode; label: string }[] = [
  { value: "keep", label: "変更しない" },
  { value: "on", label: "すべて有効" },
  { value: "off", label: "すべて無効" },
];

export const BulkChangeModal: FC<Props> = ({ open, targetCount, industryId, onClose, onApply }) => {
  // InputDatePicker(nonControl) が useFormContext() を参照するためのダミーフォーム。
  const formMethods = useForm();

  const [procedureMode, setProcedureMode] = useState<TChangeMode>("keep");
  const [learningMode, setLearningMode] = useState<TChangeMode>("keep");
  // カリキュラム開始日。学習アプリ=すべて有効 のときだけ指定できる (spec No.5)。
  const [curriculumStartDate, setCurriculumStartDate] = useState("");
  // 受験予定 (examId or "keep")。
  const [examMode, setExamMode] = useState<string>(EXAM_KEEP);
  const [examScheduleYm, setExamScheduleYm] = useState("");

  // 開いた瞬間 (false → true) に初期状態へリセットする。
  const prevOpen = useRef(open);
  useEffect(() => {
    if (open && !prevOpen.current) {
      setProcedureMode("keep");
      setLearningMode("keep");
      setCurriculumStartDate("");
      setExamMode(EXAM_KEEP);
      setExamScheduleYm("");
    }
    prevOpen.current = open;
  }, [open]);

  // 学習アプリの切替に応じてカリキュラム開始日を制御する (spec No.5):
  //  - すべて有効: 未入力なら既定値(翌週 or 当週の月曜)をセット
  //  - 変更しない / すべて無効: 値をクリアして入力不可に戻す
  const handleLearningMode = (mode: TChangeMode) => {
    setLearningMode(mode);
    if (mode === "on") {
      setCurriculumStartDate((prev) => prev || getNextOrCurrentMonday().format("YYYY/MM/DD"));
    } else {
      setCurriculumStartDate("");
    }
  };

  // 受験予定の選択肢: 選択行の受験分野(industryId)の有効な試験一覧 (table の受験予定列と同じ)。
  const { data: examData } = useLearningProgressExamList({ industryId: industryId ?? 0 });
  const exams = useMemo(() => examData?.data ?? [], [examData]);
  const examOptions = useMemo<TOption[]>(
    () => exams.filter((e) => e.status === EXAM_STATUS.ACTIVE).map((e) => ({ value: e.id, label: e.periodName })),
    [exams],
  );
  // Select の onChange は option.value(=e.id の数値)をそのまま渡すため、examMode は数値文字列/数値が混在しうる。
  // table 側 (ExamScheduleCell) と同様、突合は必ず String() で両辺を揃えてから比較する (型不一致で常に false になるのを防ぐ)。
  const selectedExam = useMemo(() => exams.find((e) => String(e.id) === String(examMode)), [exams, examMode]);
  // PT(筆記)は受験予定年月を自動補完して読み取り専用、CBT は手入力 (spec No.7)。
  const isPaperTest = selectedExam?.examMethod === EXAM_METHOD.PAPER_TEST;

  const handleExamMode = (value: string) => {
    setExamMode(value);
    if (value === EXAM_KEEP) {
      setExamScheduleYm("");
      return;
    }
    const exam = exams.find((e) => String(e.id) === String(value));
    // PT は examMonth を自動補完、CBT は空にして手入力させる。
    setExamScheduleYm(exam?.examMethod === EXAM_METHOD.PAPER_TEST && exam.examMonth ? dayjs(exam.examMonth).format("YYYY/MM") : "");
  };

  // 週数表示 (spec No.8/No.9)。カリキュラム開始日(No.5)未入力なら "-"。
  const { getSubjectCountByIndustryId } = useGetIndustryCurriculumList();
  const availableWeeks = calculateAvailableLearningWeeks(curriculumStartDate || null, examScheduleYm || null);
  const subjectCount = industryId != null ? getSubjectCountByIndustryId(industryId) : 0;
  const hasStartDate = !!curriculumStartDate;

  // 何も選んでいない (全項目「変更しない」) なら一括変更は不可にする。
  const hasChange = procedureMode !== "keep" || learningMode !== "keep" || examMode !== EXAM_KEEP;

  const handleApply = () => {
    const patch: Partial<TBulkImportRow> = {};
    if (procedureMode !== "keep") patch.procedureManagement = procedureMode === "on";
    if (learningMode !== "keep") {
      patch.learningApp = learningMode === "on";
      // 学習アプリ OFF / 変更時はカリキュラム開始日も合わせて更新 (ON のときだけ日付、それ以外はクリア)。
      patch.curriculumStartDate = learningMode === "on" ? curriculumStartDate : "";
    }
    if (examMode !== EXAM_KEEP) {
      patch.examId = Number(examMode);
      patch.examScheduleYm = examScheduleYm;
    }
    // 学習アプリを「すべて無効」にする場合は、カリキュラム開始日 / 受験予定 / 受験予定年月 を併せてクリアする
    // (行トグル OFF と同挙動)。上の examMode 指定より後に置き、OFF を優先させる。
    if (learningMode === "off") {
      patch.curriculumStartDate = "";
      patch.examId = null;
      patch.examScheduleYm = "";
    }
    onApply(patch);
    onClose();
  };

  return (
    <Modal open={open} onClose={onClose} sx={{ display: "flex", justifyContent: "center", alignItems: "center" }}>
      <ModalContainer>
        <FormProvider {...formMethods}>
          {/* ヘッダー */}
          <Stack direction="row" alignItems="flex-start" justifyContent="space-between" px={3} pt={3} flexShrink={0}>
            <Typography variant="head20Bold" color={text.primary}>
              取り込む外国人情報の一括変更
            </Typography>
            <IconBox onClick={onClose} sx={{ p: 0, mt: 0.5 }}>
              <IconClose color={black[80]} fontSize={24} />
            </IconBox>
          </Stack>

          {/* スクロール領域 (Figma: モーダル内余白 24px, カード間 24px) */}
          <Stack px={3} pt={2} pb={1} gap={3} sx={{ overflowY: "auto", flex: 1 }}>
            {/* 対象件数 (Figma: カード1まで 40px → gap24 + mb16) */}
            <Typography variant="cap12Light" color={text.secondary} mb={2}>
              対象：
              <Box component="span" sx={{ fontWeight: 700, color: text.primary }}>
                {targetCount}件
              </Box>
              　値を選んだ項目のみ反映されます（既定は「変更しない」）
            </Typography>

            {/* 手続き管理 (No.3) */}
            <Card>
              <Row label="手続き管理">
                <ChangeSegmented value={procedureMode} onChange={setProcedureMode} />
              </Row>
            </Card>

            {/* 学習セクション (No.4 / No.5) */}
            <Card label="学習">
              <Row label="学習アプリ">
                <ChangeSegmented value={learningMode} onChange={handleLearningMode} />
              </Row>
              <RowDivider />
              <Row label="カリキュラム開始日">
                <Box width={CONTROL_WIDTH}>
                  <InputDatePicker
                    nonControl
                    format="YYYY/MM/DD"
                    placeholder="----/--/--"
                    // No.4 が「すべて有効」のときのみ指定可 (spec No.5)。
                    disabled={learningMode !== "on"}
                    value={curriculumStartDate ? dayjs(curriculumStartDate, "YYYY/MM/DD") : null}
                    onChange={(v) => setCurriculumStartDate(isDayjs(v) && v.isValid() ? v.format("YYYY/MM/DD") : "")}
                  />
                </Box>
              </Row>
              <Typography variant="cap12Light" color={text.tertiary} mt={1}>
                日付を指定する場合は、学習アプリを「すべて有効」に設定してください。
              </Typography>
            </Card>

            {/* 受験セクション (No.6 / No.7 / No.8 / No.9) */}
            <Card label="受験">
              <Row label="受験予定">
                <Box width={CONTROL_WIDTH}>
                  <Select
                    placeholder="変更しない"
                    clearable={false}
                    options={[{ value: EXAM_KEEP, label: "変更しない" }, ...examOptions]}
                    value={examMode}
                    disabled={industryId == null}
                    onChange={handleExamMode}
                  />
                </Box>
              </Row>
              <RowDivider />
              <Row label="受験予定年月">
                <Box width={CONTROL_WIDTH}>
                  <InputDatePicker
                    nonControl
                    views={["year", "month"]}
                    openTo="month"
                    format="YYYY/MM"
                    placeholder="----/--"
                    // 受験予定(No.6)未選択なら不可。PT は自動補完済みで読み取り専用、CBT は手入力 (spec No.7)。
                    disabled={examMode === EXAM_KEEP}
                    readonly={isPaperTest}
                    value={examScheduleYm ? dayjs(examScheduleYm, "YYYY/MM") : null}
                    onChange={(v) => setExamScheduleYm(isDayjs(v) && v.isValid() ? v.format("YYYY/MM") : "")}
                  />
                </Box>
              </Row>
              <RowDivider />
              {/* 週数表示は隣接 2 行をまとめて間隔を詰める (spec No.8 / No.9) */}
              <Stack gap={0.5}>
                <Row label="大まかな学習可能な期間（週数）" dense>
                  <Box width={CONTROL_WIDTH}>
                    <Typography variant="body14Semi" color={text.primary}>
                      {hasStartDate && availableWeeks !== null ? `${availableWeeks}週間` : "-"}
                    </Typography>
                  </Box>
                </Row>
                <Row label="学習に必要な期間（週数）" dense>
                  <Box width={CONTROL_WIDTH}>
                    <Typography variant="body14Semi" color={text.primary}>
                      {hasStartDate ? `${subjectCount + MOCK_EXAM_WEEKS}週間（${subjectCount} WEEK + 模擬試験）` : "-"}
                    </Typography>
                  </Box>
                </Row>
              </Stack>
            </Card>
          </Stack>

          {/* フッター (No.10 / No.11 / Figma: 上下 24px) */}
          <Stack direction="row" justifyContent="center" gap={2} px={3} py={3} flexShrink={0}>
            <Button variant="outline" size="md" onClick={onClose}>
              <Typography variant="body14Semi">キャンセル</Typography>
            </Button>
            <Button size="md" startIcon={<IconCheck />} onClick={handleApply} disabled={!hasChange}>
              <Typography variant="body14Semi">一括変更</Typography>
            </Button>
          </Stack>
        </FormProvider>
      </ModalContainer>
    </Modal>
  );
};

// 右側コントロールの共通幅 (Select / DatePicker / 週数の値)。値を左揃えにして入力欄と縦に揃える。
const CONTROL_WIDTH = 220;

// ラベル(左) + コントロール(右) の 1 行。入力行は高さ 36px に揃える。
// dense: 週数など値のみの行 (Figma で行高が低い) は最小高さを外す。
const Row: FCC<{ label: string; dense?: boolean }> = ({ label, dense, children }) => (
  <Stack direction="row" alignItems="center" justifyContent="space-between" gap={2} minHeight={dense ? undefined : 36}>
    <Typography variant="body14Semi" color={text.primary}>
      {label}
    </Typography>
    {children}
  </Stack>
);

// 行と行の区切り線 (セクション内 / Figma: 上下 8px)。
const RowDivider = () => <Divider sx={{ borderColor: divider.middle, my: 1 }} />;

// 薄いグレー塗りのカード (手続き管理 / 学習 / 受験)。任意でセクション見出しを付ける。
// Figma: 内側余白 16px、見出し下 8px。
const Card: FCC<{ label?: string }> = ({ label, children }) => (
  <Stack sx={{ backgroundColor: background.primary, borderRadius: rounded.md, p: 2 }}>
    {label && (
      <Typography variant="cap12" color={text.secondary} mb={1}>
        {label}
      </Typography>
    )}
    {children}
  </Stack>
);

// 3 値セグメント (変更しない / すべて有効 / すべて無効)。選択中はブランドティールで塗り、未選択はティール文字。
const ChangeSegmented: FC<{ value: TChangeMode; onChange: (_mode: TChangeMode) => void }> = ({ value, onChange }) => (
  <Stack
    direction="row"
    flexShrink={0}
    sx={{ border: `1px solid ${primary[700]}`, borderRadius: rounded.sm, overflow: "hidden", backgroundColor: background.white }}
  >
    {SEGMENTS.map((seg, i) => {
      const active = value === seg.value;
      return (
        <Box
          key={seg.value}
          onClick={() => onChange(seg.value)}
          sx={{
            // Figma: セグメント幅 ≈100px / 高さ 32px。
            minWidth: 96,
            px: 2,
            py: 0.75,
            textAlign: "center",
            cursor: "pointer",
            userSelect: "none",
            transition: "background-color .15s ease",
            borderLeft: i === 0 ? "none" : `1px solid ${primary[700]}`,
            backgroundColor: active ? button.primary : "transparent",
            "&:hover": { backgroundColor: active ? button.primary : primary[900] },
          }}
        >
          <Typography variant="body14Semi" color={active ? text.white : button.primary} whiteSpace="nowrap">
            {seg.label}
          </Typography>
        </Box>
      );
    })}
  </Stack>
);

const ModalContainer = styled(Box)`
  width: 600px;
  background-color: ${background.white};
  box-shadow: 0px 8px 24px 0px #00000026;
  border-radius: 8px;
  max-height: calc(100vh - 48px);
  display: flex;
  flex-direction: column;
  outline: none;
  &:focus {
    outline: none;
  }
`;
